import type { DatasetRowCountOutput } from '@cotera/client/app/etc/api/typed-client';

/**
 * What the header's row count is leaving out, or null when it is the whole
 * story.
 *
 * Two different omissions, deliberately worded apart. A *sample* is the
 * dataset's own doing: its kind copies part of itself into the artifact the
 * browser reads, so no filter the user clears will bring the rest into view. A
 * *narrowed* view is the user's own doing and is one click from being undone.
 * Calling the second a sample would misdescribe what the grid can reach; saying
 * nothing about the first lets the header pass a slice off as the dataset.
 *
 * Sampling wins when both apply — it is the harder limit, and the only one the
 * reader has no way of discovering.
 */
export const datasetRowCountNote = (params: {
  /** Rows the grid is showing, after filters and any ad-hoc query. */
  readonly shownRows: number | null;
  readonly rowCount: DatasetRowCountOutput | null;
}): string | null => {
  const { shownRows, rowCount } = params;
  // No answer from the server, or a kind that would not pay for one. An absent
  // disclosure is recoverable; an invented one is not.
  if (rowCount === null || rowCount.totalRows === null) {
    return null;
  }
  const { artifactRows, totalRows } = rowCount;
  if (artifactRows !== null && totalRows > artifactRows) {
    return `Showing a ${artifactRows.toLocaleString()} row sample of ${totalRows.toLocaleString()} rows`;
  }
  if (shownRows !== null && shownRows < totalRows) {
    return `${shownRows.toLocaleString()} of ${totalRows.toLocaleString()} rows match the current view`;
  }
  return null;
};
